const dialog = require('../dialog');
const validator = require('../validator');

/* ============================================================================
 * CONSTANTS
 * ========================================================================== */
const SELECTORS = {
    OTP_DIALOG: '#prequalification-otp-modal',
    OTP_FORM: '#prequalification-otp-form',
    OTP_INPUT: 'input[name$="_otp"]',
    RESEND_BUTTON: '.resend-otp-button',
    CANCEL_BUTTON: '.cta-cancelotp',
    BIRTHDATE_INPUT: 'input[id$="_prequalification_birthdate"]',
    ERROR_MESSAGE: '.otp-error-message',
    SUCCESS_MESSAGE: '.otp-success-message',
    MAX_RESEND_MESSAGE: '.otp-maxresend-message',
    TEST_DISPLAY: '.otp-test-display',
    OTP_INSTRUCTIONS: '.otp-instructions',
};

const TIMEOUTS = {
    SUCCESS_FADE: 3000,
    REDIRECT_DELAY: 2000,
    FOCUS_DELAY: 100,
};

const MESSAGES = {
    DEFAULT_ERROR: 'An error occurred. Please try again.',
    DEFAULT_SUCCESS: 'New OTP sent successfully!',
    INVALID_OTP: 'Invalid OTP code. Please try again.',
    RESEND_FAILED: 'Failed to resend OTP',
};

const DEBUG_MODE = false; // Set to true to enable console logging

// Version: v3.0.0 (Optimized - Nov 7, 2025)

/* ============================================================================
 * HELPER FUNCTIONS
 * ========================================================================== */

/**
 * Logs debug messages if DEBUG_MODE is enabled
 * @private
 * @param {string} message - The message to log
 * @param {*} data - Optional data to log
 */
function debugLog(message, data) {
    if (DEBUG_MODE) {
        // eslint-disable-next-line no-console
        console.log(message, data || '');
    }
}

/**
 * Clears all message containers
 * @private
 * @param {jQuery} $context - The context to search within (defaults to document)
 */
function clearAllMessages($context) {
    const context = $context || $(document);
    context.find(SELECTORS.ERROR_MESSAGE).hide().text('');
    context.find(SELECTORS.SUCCESS_MESSAGE).hide().text('');
    context.find(SELECTORS.MAX_RESEND_MESSAGE).hide().text('');
}

/**
 * Shows an error message
 * @private
 * @param {jQuery} $container - The error container element
 * @param {string} message - The error message to display
 */
function showErrorMessage($container, message) {
    $container.text(message || MESSAGES.DEFAULT_ERROR).show();
}

/**
 * Shows a success message with auto-fade
 * @private
 * @param {jQuery} $container - The success container element
 * @param {string} message - The success message to display
 * @param {number} fadeDelay - Delay before fading (optional)
 */
function showSuccessMessage($container, message, fadeDelay) {
    $container.text(message || MESSAGES.DEFAULT_SUCCESS).show();
    if (fadeDelay !== false) {
        setTimeout(() => {
            $container.fadeOut();
        }, fadeDelay || TIMEOUTS.SUCCESS_FADE);
    }
}

/**
 * Reinitializes form validation
 * @private
 * @param {jQuery} $form - The form element
 * @param {Object} formValidator - The form validator object
 */
function reinitializeValidation($form, formValidator) {
    if ($form.length && formValidator) {
        validator.init();
    }
}

/**
 * Clears and focuses OTP input field
 * @private
 */
function resetOtpInput() {
    $(SELECTORS.OTP_INPUT).val('').focus();
}

/**
 * Formats the error message with attempts remaining
 * @private
 * @param {string} message - Base error message
 * @param {number} attemptsRemaining - Number of attempts remaining
 * @returns {string} Formatted error message
 */
function formatErrorWithAttempts(message, attemptsRemaining) {
    if (attemptsRemaining) {
        const plural = attemptsRemaining > 1 ? 's' : '';
        return `${message} (${attemptsRemaining} attempt${plural} remaining)`;
    }
    return message || MESSAGES.INVALID_OTP;
}

/* ============================================================================
 * EVENT HANDLERS
 * ========================================================================== */

/**
 * @private
 * @function
 * @description Handles OTP form submission inside the dialog
 */
function handleOtpFormSubmit() {
    $(document).on('submit', SELECTORS.OTP_FORM, (e) => {
        e.preventDefault();

        const $form = $(e.currentTarget);
        const $errorContainer = $form.find(SELECTORS.ERROR_MESSAGE);
        const $submitButton = $form.find('button[type="submit"]');

        // Clear previous messages and disable submit button
        clearAllMessages($form);
        $submitButton.prop('disabled', true);

        // Make AJAX call to validate OTP
        $.ajax({
            url: $form.attr('action'),
            type: 'POST',
            data: $form.serialize(),
            dataType: 'json',
            success(response) {
                debugLog('[OTP Validate] Response received:', response);

                // Check if response has redirect flag (PRIORITY: handle redirects first)
                if (response && response.redirect === true && response.redirectUrl) {
                    debugLog('[OTP Validate] Redirecting to:', response.redirectUrl);
                    dialog.close();
                    window.location.href = response.redirectUrl;
                    return;
                }

                // Only show error messages if no redirect flag
                if (response && response.success === false) {
                    debugLog('[OTP Validate] Validation failed, showing error');
                    const errorMsg = formatErrorWithAttempts(response.message, response.attemptsRemaining);
                    showErrorMessage($errorContainer, errorMsg);
                    $submitButton.prop('disabled', false);
                    resetOtpInput();
                }
            },
            error() {
                showErrorMessage($errorContainer, MESSAGES.DEFAULT_ERROR);
                $submitButton.prop('disabled', false);
            },
        });
    });
}

/**
 * @private
 * @function
 * @description Handles resend OTP button click
 */
function handleResendOtp() {
    $(document).on('click', SELECTORS.RESEND_BUTTON, (e) => {
        e.preventDefault();
        e.stopPropagation();

        const $button = $(e.currentTarget);
        const $form = $(SELECTORS.OTP_FORM);
        const $errorContainer = $(SELECTORS.ERROR_MESSAGE);
        const $successContainer = $(SELECTORS.SUCCESS_MESSAGE);
        const $maxResendContainer = $(SELECTORS.MAX_RESEND_MESSAGE);
        const resendUrl = $button.data('url') || Urls.experianOTPResend;
        const originalText = $button.text();
        let formValidator = null;

        // Clear previous messages
        clearAllMessages();

        // Clear validation errors and store validator reference
        if ($form.length) {
            formValidator = $form.data('validator');
            if (formValidator) {
                formValidator.resetForm();
            }
            $form.find('.error').removeClass('error');
            $form.off('focusout.validate focusin.validate');
        }

        // Disable resend button (in next tick to avoid validation trigger)
        setTimeout(() => {
            $button.prop('disabled', true).text('Sending...');
        }, 0);

        // Make AJAX call to resend OTP
        $.ajax({
            url: resendUrl,
            type: 'POST',
            dataType: 'json',
            success(response) {
                if (response.success) {
                    showSuccessMessage($successContainer, response.message);
                    resetOtpInput();
                    $button.prop('disabled', false).text(originalText);
                    reinitializeValidation($form, formValidator);
                } else if (response.maxResendReached) {
                    $maxResendContainer.text(response.message).show();
                    setTimeout(() => {
                        dialog.close();
                        window.location.href = response.redirectUrl || Urls.prequalificationForm;
                    }, TIMEOUTS.REDIRECT_DELAY);
                } else if (response.serviceError && response.redirectUrl) {
                    dialog.close();
                    window.location.href = response.redirectUrl;
                } else {
                    showErrorMessage($errorContainer, response.message || MESSAGES.RESEND_FAILED);
                    $button.prop('disabled', false).text(originalText);
                    reinitializeValidation($form, formValidator);
                }
            },
            error() {
                showErrorMessage($errorContainer, MESSAGES.DEFAULT_ERROR);
                $button.prop('disabled', false).text(originalText);
                reinitializeValidation($form, formValidator);
            },
        });
    });
}

/**
 * @private
 * @function
 * @description Opens the OTP verification dialog
 */
function openOtpDialog() {
    const $dialogContainer = $(SELECTORS.OTP_DIALOG);
    const dialogUrl = $dialogContainer.attr('data-url');

    dialog.open({
        url: dialogUrl,
        options: {
            title: Resources.AUTHENTICATION || 'Verify Identity',
            width: 400,
            draggable: false,
            modal: true,
            closeOnEscape: false,
            dialogClass: 'no-close prequalification-otp-dialog',
            open() {
                validator.init();
                // Focus on OTP input field after dialog opens
                setTimeout(() => {
                    $(SELECTORS.OTP_INPUT).focus();
                }, TIMEOUTS.FOCUS_DELAY);
            },
            beforeClose() {
                // Prevent closing when clicking outside
                return false;
            },
        },
    });
}

/**
 * @private
 * @function
 * @description Handles cancel button click in OTP modal
 * Closes modal and redirects to prequalification form
 */
function handleCancelOtp() {
    $(document).on('click', SELECTORS.CANCEL_BUTTON, (e) => {
        e.preventDefault();
        dialog.close();
        window.location.href = Urls.prequalificationForm;
    });
}

/**
 * @private
 * @function
 * @description Binds the events of the prequalification date of birth field
 */
function initDatePicker() {
    $(SELECTORS.BIRTHDATE_INPUT).datepicker({
        changeMonth: true,
        changeYear: true,
        yearRange: '-120:+0',
        minDate: '-120y',
        maxDate: '+0d',
        onClose() {
            $(this).valid();
        },
    });
}

/**
 * @private
 * @function
 * @description Checks if we're on MFA page and should auto-open OTP modal
 */
function checkAndOpenModal() {
    const $modalContainer = $(SELECTORS.OTP_DIALOG);

    // Check if modal container exists and has open-modal flag set to true
    // jQuery's .data() auto-converts "true" string to boolean true
    if ($modalContainer.length && $modalContainer.data('open-modal') === true) {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
            openOtpDialog();
        }, TIMEOUTS.FOCUS_DELAY);
    }
}

/* ============================================================================
 * INITIALIZATION
 * ========================================================================== */

/**
 * @public
 * @function
 * @description Initializes all prequalification events
 */
function init() {
    initDatePicker();
    handleOtpFormSubmit();
    handleResendOtp();
    handleCancelOtp();
    checkAndOpenModal();
}

/* ============================================================================
 * PUBLIC API
 * ========================================================================== */

module.exports = {
    init,
    openOtpDialog,
};
